import qs from "qs";
import React, { useEffect, useState } from "react";
import { TailSpin } from "react-loader-spinner";
import {
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
} from "recharts";
import api from "../api";
import { useFilters } from "./FilterContext";

interface EventData {
  name: string;
  eventCount: number;
}

const ByEvents: React.FC = () => {
  const [data, setData] = useState<EventData[]>([]);
  const [loading, setLoading] = useState(true);

  const { filters } = useFilters();


  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);

      console.log("filters", filters);

      try {
        const { data: res } = await api.get("/dashboard/by-event", {
          params: {
            routeIds: filters.routeIds, eventTypes: filters.eventTypes, ...(filters.startDate && { startDate: filters.startDate }),
            ...(filters.endDate && { endDate: filters.endDate })
          },
          paramsSerializer: (params) =>
            qs.stringify(params, { arrayFormat: "comma" }),
        });

        // Convert string 'eventCount' to number & sort descending
        const eventTypes = res
          .map((item: any) => ({
            name: item.name,
            eventCount: Number(item.eventCount),
          }))
          .sort((a: EventData, b: EventData) => b.eventCount - a.eventCount);

        setData(eventTypes);
      } finally {
        setLoading(false);
      }

    };

    fetchData();
  }, [filters]);

  // Color palette for slices
  const COLORS = [
    "#0088FE",
    "#00C49F",
    "#FFBB28",
    "#FF8042",
    "#845EC2",
    "#FF5E78",
    "#45B39D",
    "#EB984E",
    "#A93226",
    "#D4AC0D",
    "#7D3C98",
  ];

  // Custom tooltip to show count & percentage
  const CustomTooltip = (props: TooltipProps<number, string>) => {
    const { active, payload } = props;
    if (active && payload && payload.length) {
      const { name, eventCount } = payload[0].payload;
      const total = data.reduce((sum, item) => sum + item.eventCount, 0);
      const percentage = ((eventCount / total) * 100).toFixed(1);

      return (
        <div className="bg-white border border-gray-300 p-2 rounded-md">
          <strong className="block">{name}</strong>
          <span className="block">Count: {eventCount}</span>
          <span className="block">({percentage}%)</span>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6 w-full h-[450px]">
      {/* Chart Title */}
      <h3 className="text-left text-2xl font-bold text-gray-800 mb-2">
        Events by Type
      </h3>

      {loading ? (
        <div className="flex items-center justify-center w-full h-[300px]">
          <TailSpin
            height={80}
            width={80}
            color="#2563eb"
            strokeWidth={2}
            ariaLabel="oval-loading"
            visible={true}
          />
        </div>
      ) : (
        // Chart Container
        <div className="w-full h-[340px]">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                dataKey="eventCount"
                nameKey="name"
                cx="50%"
                cy="50%"
                innerRadius={70}
                outerRadius={120}
              >
                {data.map((_, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={COLORS[index % COLORS.length]}
                  />
                ))}
              </Pie>

              {/* Custom tooltip with count & percentage */}
              <Tooltip content={<CustomTooltip />} />

              {/* Legend at the bottom */}
              <Legend
                verticalAlign="bottom"
                height={36}
                wrapperStyle={{
                  fontSize: "14px",
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      )}
    </div >
  );
};

export default ByEvents;
